/**
 * Sipariş Önerisi — Sprint 32 (Consumption Velocity).
 *
 * Kullanıcı: "kıyma haftada 10 kg gidiyor, kola haftada 500 adet
 * gidiyor... ortalama değerlerı tespıt etsek sıparıs gecılse memnun
 * olur." Sistem tüketim hızını analiz edip "şu kadar al" önerisi sunar.
 */
import {
  Badge, Group, Loader, Paper, Select,
  Stack, Switch, Table, Text, Title,
} from "@mantine/core";
import { IconTrendingUp, IconAlertTriangle } from "@tabler/icons-react";
import { useState } from "react";
import {
  useConsumptionSummary, useReorderSuggestions,
} from "../../../shared/api";
import { MoneyDisplay, PageHeader } from "../../../shared/ui";

export function ReorderSuggestionsPage() {
  const [days, setDays] = useState(7);
  const [targetDays, setTargetDays] = useState(14);
  const [onlyLow, setOnlyLow] = useState(false);

  const { data: consumption } = useConsumptionSummary(days);
  const { data: suggestions, isLoading } = useReorderSuggestions(targetDays, onlyLow);

  return (
    <Stack gap="lg">
      <PageHeader
        title="🛒 Sipariş Önerisi"
        subtitle="Tüketim hızına göre tedarikçi siparişi önerileri"
      />

      <Paper p="md" radius="md" withBorder>
        <Group>
          <Select
            label="Tüketim Penceresi"
            value={`${days}`}
            onChange={(v) => v && setDays(Number(v))}
            data={[
              { value: "7", label: "Son 7 gün" },
              { value: "14", label: "Son 14 gün" },
              { value: "30", label: "Son 30 gün" },
            ]}
            w={200}
          />
          <Select
            label="Hedef Süre (kaç günlük al)"
            value={`${targetDays}`}
            onChange={(v) => v && setTargetDays(Number(v))}
            data={[
              { value: "7", label: "1 hafta" },
              { value: "14", label: "2 hafta (önerilen)" },
              { value: "30", label: "1 ay" },
            ]}
            w={250}
          />
          <Switch
            label="Sadece kritik / önerilen"
            checked={onlyLow}
            onChange={(e) => setOnlyLow(e.currentTarget.checked)}
            mt="lg"
          />
        </Group>
      </Paper>

      {/* Tüketim Özeti */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">
          <Group gap="xs">
            <IconTrendingUp size={20} />
            En Çok Tüketilen Hammaddeler (Son {days} gün)
          </Group>
        </Title>
        {consumption?.items && consumption.items.length > 0 ? (
          <Table.ScrollContainer minWidth={620}>
          <Table striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Hammadde</Table.Th>
                <Table.Th ta="right">Toplam Tüketim</Table.Th>
                <Table.Th ta="right">Günlük Ortalama</Table.Th>
                <Table.Th ta="right">Haftalık Ortalama</Table.Th>
                <Table.Th ta="right">Parasal Değer</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {consumption.items.map((item: any) => (
                <Table.Tr key={item.ingredient_id}>
                  <Table.Td fw={500}>{item.ingredient_name}</Table.Td>
                  <Table.Td ta="right">
                    {parseFloat(item.total_consumed).toFixed(2)} {item.unit}
                  </Table.Td>
                  <Table.Td ta="right">
                    {parseFloat(item.avg_per_day).toFixed(2)} {item.unit}/gün
                  </Table.Td>
                  <Table.Td ta="right" fw={600}>
                    {parseFloat(item.avg_per_week).toFixed(1)} {item.unit}/hafta
                  </Table.Td>
                  <Table.Td ta="right" c="red">
                    <MoneyDisplay minor={item.value_minor} />
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        ) : (
          <Text c="dimmed">Tüketim verisi yok</Text>
        )}
      </Paper>

      {/* Sipariş Önerileri */}
      <Paper p="lg" radius="md" withBorder>
        <Title order={4} mb="md">
          <Group gap="xs">
            <IconAlertTriangle size={20} color="orange" />
            Sipariş Önerileri ({targetDays} gün için)
          </Group>
        </Title>
        {isLoading ? <Loader /> : null}
        {suggestions?.items && suggestions.items.length > 0 ? (
          <Table.ScrollContainer minWidth={760}>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Hammadde</Table.Th>
                <Table.Th ta="right">Günlük Tüketim</Table.Th>
                <Table.Th ta="right">{targetDays}g İhtiyaç</Table.Th>
                <Table.Th ta="right">Mevcut Stok</Table.Th>
                <Table.Th ta="right">Önerilen Sipariş</Table.Th>
                <Table.Th ta="right">Tahmini Maliyet</Table.Th>
                <Table.Th>Durum</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {suggestions.items.map((s: any) => {
                const suggested = parseFloat(s.suggested_quantity);
                return (
                  <Table.Tr key={s.ingredient_id}>
                    <Table.Td fw={500}>{s.ingredient_name}</Table.Td>
                    <Table.Td ta="right">
                      {parseFloat(s.avg_daily_consumption).toFixed(2)} {s.unit}
                    </Table.Td>
                    <Table.Td ta="right">
                      {parseFloat(s.projected_need).toFixed(2)} {s.unit}
                    </Table.Td>
                    <Table.Td ta="right">
                      {parseFloat(s.current_stock).toFixed(2)} {s.unit}
                    </Table.Td>
                    <Table.Td ta="right" fw={700} c={suggested > 0 ? "orange" : "dimmed"}>
                      {suggested.toFixed(2)} {s.unit}
                    </Table.Td>
                    <Table.Td ta="right" c="red">
                      <MoneyDisplay minor={s.suggested_value_minor} />
                    </Table.Td>
                    <Table.Td>
                      {suggested > 0 ? (
                        <Badge color="orange">Sipariş Ver</Badge>
                      ) : (
                        <Badge color="green" variant="light">Yeterli</Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
          </Table.ScrollContainer>
        ) : (
          <Text c="dimmed" py="md">
            {onlyLow ? "Şu an kritik/öneri yok 🎉" : "Hiç hammadde yok"}
          </Text>
        )}
      </Paper>
    </Stack>
  );
}
